import { Link, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCreateInvestigation } from '@/application/services/investigation.service'
import type { SupportedLangCode } from '@/application/i18n/language'
import type { TaxonomyTerm } from '@/domains/types'
import { Button } from '@/ui/components/common/Button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/ui/components/forms/Form'
import { ImageField } from '@/ui/components/forms/ImageField'
import { Input } from '@/ui/components/forms/Input'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { TaxonomyTermPicker } from '@/ui/components/forms/TaxonomyTermPicker'
import { SourceLanguageSelector } from '@/ui/components/translations/SourceLanguageSelector'

const makeCreateInvestigationSchema = (
  translate: (k: string, opts?: Record<string, unknown>) => string,
) =>
  z.object({
    name: z
      .string()
      .min(2, translate('validation.name_min'))
      .max(250, translate('validation.name_max')),
    description: z.string().max(20000, translate('validation.desc_max')),
    investigationId: z
      .string()
      .min(2, translate('validation.id_min'))
      .max(200, translate('validation.id_max')),
    imageUrl: z
      .string()
      .max(2048)
      .refine(
        (value) =>
          !value ||
          value.startsWith('/') ||
          (() => {
            try {
              new URL(value)
              return true
            } catch {
              return false
            }
          })(),
        { message: translate('validation.image_url') },
      )
      .optional()
      .or(z.literal('')),
  })

type CreateInvestigationValues = z.infer<ReturnType<typeof makeCreateInvestigationSchema>>

/**
 * Section that owns the entire investigation-creation form: schema, react-hook-form
 * state, the `useCreateInvestigation` data hook, the category term picker,
 * the submit handler (with toast feedback + navigation), and the surrounding card UI.
 */
export function CreateInvestigationFormSection() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { mutateAsync: createInvestigation, isPending } = useCreateInvestigation()
  const [sourceLocale, setSourceLocale] = useState<SupportedLangCode>('fr')
  // The linked category terms live outside RHF: they are terms, not strings.
  const [categoryTerms, setCategoryTerms] = useState<TaxonomyTerm[]>([])

  const schema = useMemo(() => makeCreateInvestigationSchema(t), [t])

  const form = useForm<CreateInvestigationValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      investigationId: '',
      imageUrl: '',
    },
  })

  const handleSubmit = async (values: CreateInvestigationValues) => {
    try {
      const result = await createInvestigation({
        ...values,
        sourceLocale,
        categoryTermIds: categoryTerms.map((term) => term.id),
      })
      toast.success(t('admin.form_create_success', { name: values.name }))
      navigate({
        to: '/manage/investigations',
        search: { statusFilter: result.status ?? 'pending' },
      })
    } catch (error: unknown) {
      toast.error(
        t('admin.form_create_error', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.form_create_title')}</CardTitle>
        <CardDescription>{t('admin.form_create_subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <SourceLanguageSelector value={sourceLocale} onChange={setSourceLocale} />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_name_label')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('admin.form_name_placeholder')} {...field} />
                  </FormControl>
                  <FormDescription>{t('admin.form_name_desc')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_desc_label')}</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('admin.form_desc_placeholder')}
                      disabled={isPending}
                      onUploadError={(message) => toast.error(message)}
                    />
                  </FormControl>
                  <FormDescription>{t('admin.form_desc_helper')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="investigationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_id_label')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('admin.form_id_placeholder')} {...field} />
                  </FormControl>
                  <FormDescription>{t('admin.form_id_desc')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <div>
                <label className="text-sm font-medium leading-none">
                  {t('admin.form_category_label')}
                </label>
              </div>
              <TaxonomyTermPicker
                locale={sourceLocale}
                value={categoryTerms}
                onChange={setCategoryTerms}
                placeholder={t('admin.form_category_placeholder')}
                disabled={isPending}
              />
              <p className="text-sm text-muted-foreground">{t('admin.form_category_desc')}</p>
            </div>

            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_image_label')}</FormLabel>
                  <FormControl>
                    <ImageField
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormDescription>{t('admin.form_image_desc')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-end gap-2 pt-4">
              <Link to="/manage/investigations" search={{ statusFilter: 'active' }}>
                <Button variant="outline" type="button">
                  {t('admin.form_cancel')}
                </Button>
              </Link>
              <Button type="submit" isLoading={isPending}>
                {t('admin.form_create_submit')}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
